import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInstances } from '@/store/actions/typemark';
import { Button } from '@/components/ui/Button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/Table';

const PAGE_SIZE = 10;

const InstanceTable = ({ id }) => {
  const [pageIndex, setPageIndex] = useState(1);
  const dispatch = useDispatch();
  const { instances } = useSelector((state) => state.typeMark);

  useEffect(() => {
    if (!id) return;
    dispatch(fetchInstances(id, pageIndex, PAGE_SIZE));
  }, [pageIndex, id]);

  const items = instances.items || [];
  const totalCount = instances.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const hasPrev = pageIndex > 1;
  const hasNext = pageIndex * PAGE_SIZE < totalCount;

  const cols = [
    { key: 'id', header: 'Identifikátor', accessor: (row) => row.id },
    {
      key: 'buildingObject',
      header: 'St. objekt',
      accessor: (row) => row?.building_object?.title || '',
    },
    { key: 'storey', header: 'Podlaží', accessor: (row) => row?.storey?.title || '' },
    { key: 'room', header: 'Místnost', accessor: (row) => row?.room?.title || '' },
  ];

  return (
    <div className="rounded border border-neutral-150 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {cols.map((col, i) => (
              <TableHead
                key={col.key}
                className={i < cols.length - 1 ? 'border-r border-neutral-150' : ''}
              >
                {col.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length > 0 ? (
            items.map((row, rowIdx) => (
              <TableRow key={row.id || rowIdx}>
                {cols.map((col, i) => (
                  <TableCell
                    key={col.key}
                    className={i < cols.length - 1 ? 'border-r border-neutral-150' : ''}
                  >
                    {col.accessor(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={cols.length} className="text-center py-8 text-neutral-400">
                Žádné instance
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {totalCount > 0 && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-neutral-150 bg-neutral-25">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={!hasPrev}
              data-testid="typemark-instance-prev-page-btn"
              onClick={() => setPageIndex((p) => p - 1)}
            >
              <ChevronLeft size={14} />
            </Button>
            <span className="text-xs text-neutral-500 tabular-nums px-2">
              {pageIndex} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={!hasNext}
              data-testid="typemark-instance-next-page-btn"
              onClick={() => setPageIndex((p) => p + 1)}
            >
              <ChevronRight size={14} />
            </Button>
          </div>
          <span className="text-xs text-neutral-500">Celkem: {totalCount}</span>
        </div>
      )}
    </div>
  );
};

export default InstanceTable;
